import { router } from 'expo-router';
import { ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useNpoOnboarding } from '@/api/hooks/meHooks';
import BrandedButton from '@/components/buttons/BrandedButton';
import PreferencesForm from '@/components/forms/PreferencesForm';
import Text from '@/components/Text';
import { useAuth } from '@/contexts/authContext';
import { useColor } from '@/hooks/useColor';
import { InterestsData } from '@/lib/validations/AuthSchema';
import { i18n } from '@/utils/i18n';

const PreferencesScreen = () => {
  const colors = useColor();
  const { state } = useAuth();
  const type = state.signUpData?.loginEmail?.type;

  const { mutateAsync: onBoardingUpdate, isPending } = useNpoOnboarding(state.signUpData?.loginEmail?.type ?? 'CHURCH');

  //Locales
  const titleText = i18n.t('sign_in.nonProfit.preferences.title');
  const descriptionText = i18n.t('sign_in.nonProfit.preferences.description');
  const skipButtonText = i18n.t('mix.set_up_later');

  const defaultValues = {
    denominations: [],
    religions: [],
    programs: [],
    checkbox_selection: undefined
  };

  const onDone = async (data: InterestsData) => {
    await onBoardingUpdate(
      {
        preferenceId: state.preferenceId ?? '',
        ...(data.religions && data.religions.length > 0 && { userInterestOnReligion: data.religions }),
        ...(data.denominations && data.denominations.length > 0 && { userInterestOnDenomination: data.denominations }),
        userInterestOnProgram: data.programs,
        skipPreferenceScreen: false,
        isSetPreferenceScreen: true
      },
      {
        onSuccess: () => {
          router.replace({ pathname: '/login/notifications', params: { from: 'nonProfit' } });
        }
      }
    );
  };

  const onPressSkip = () => {
    onBoardingUpdate(
      {
        preferenceId: state.preferenceId ?? '',
        skipPreferenceScreen: true,
        isSetPreferenceScreen: false
      },
      {
        onSuccess: () => {
          router.replace({ pathname: '/login/notifications', params: { from: 'nonProfit' } });
        }
      }
    );
  };

  return (
    <SafeAreaView style={styles.contentWrapper}>
      <ScrollView contentContainerStyle={styles.scrollContainer} showsVerticalScrollIndicator={false}>
        <View style={styles.contentWrapper}>
          <View style={styles.formContainer}>
            <View style={styles.titleContainer}>
              <Text style={[styles.titleText, { color: colors.titleText }]} fontWeight="700">
                {titleText}
              </Text>
            </View>
            <Text style={[styles.descriptionText, { color: colors.titleText }]}>{descriptionText}</Text>
            <PreferencesForm
              defaultValues={defaultValues}
              onDone={onDone}
              isPending={isPending}
              maxPrograms={5}
              showDenominations={type !== 'CHARITIES'}
              showReligions={type !== 'CHARITIES'}
            />
          </View>
          <BrandedButton
            loading={isPending}
            style={styles.btnStyle}
            textStyles={styles.btnText}
            onPress={onPressSkip}
            fontWeight="450">
            {skipButtonText}
          </BrandedButton>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

export default PreferencesScreen;

const styles = StyleSheet.create({
  contentWrapper: {
    flex: 1
  },

  scrollContainer: {
    paddingHorizontal: 16,
    paddingVertical: 18,
    flexGrow: 1
  },

  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5
  },

  titleText: {
    fontSize: 22,
    textAlign: 'center'
  },

  descriptionText: {
    fontSize: 14
  },

  formContainer: {
    rowGap: 16,
    flex: 1,
    marginBottom: 20
  },

  btnStyle: {
    width: '100%',
    backgroundColor: 'transparent'
  },

  btnText: {
    color: '#00000099',
    fontSize: 14
  }
});
